import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
    Bookmark, 
    BookmarkCheck, 
    Settings, 
    X, 
    Lock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoryDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, isSubscribed, settings, updateSettings, getAuthHeaders } = useAuth();
    
    const [story, setStory] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [bookmarkId, setBookmarkId] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [readingProgress, setReadingProgress] = useState(0);
    
    const contentRef = useRef(null);
    const progressThrottleRef = useRef(null);

    const fetchStory = useCallback(async () => {
        try {
            const previewRes = await axios.get(`${API}/stories/${id}/preview`);
            setPreview(previewRes.data);

            if (user && isSubscribed()) {
                const fullRes = await axios.get(`${API}/stories/${id}`, {
                    headers: getAuthHeaders()
                });
                setStory(fullRes.data);

                const bookmarksRes = await axios.get(`${API}/bookmarks`, {
                    headers: getAuthHeaders()
                });
                const bookmark = bookmarksRes.data.find(b => b.story_id === id);
                if (bookmark) {
                    setIsBookmarked(true);
                    setBookmarkId(bookmark.id);
                }

                const progressRes = await axios.get(`${API}/progress`, {
                    headers: getAuthHeaders()
                });
                const progress = progressRes.data.find(p => p.story_id === id);
                if (progress) {
                    setReadingProgress(progress.progress);
                }
            }
        } catch (error) {
            console.error('Failed to fetch story:', error);
        } finally {
            setLoading(false);
        }
    }, [id, user, isSubscribed, getAuthHeaders]);

    useEffect(() => {
        fetchStory();
    }, [fetchStory]);

    useEffect(() => {
        if (!story || !contentRef.current) return;

        const handleScroll = () => {
            if (progressThrottleRef.current) return;

            const element = contentRef.current;
            const scrollTop = window.scrollY - element.offsetTop;
            const scrollHeight = element.scrollHeight;
            const clientHeight = window.innerHeight;
            
            const progress = Math.min(1, Math.max(0, (scrollTop + clientHeight) / scrollHeight));
            setReadingProgress(progress);

            progressThrottleRef.current = setTimeout(async () => {
                progressThrottleRef.current = null;
                try {
                    await axios.post(`${API}/progress`, {
                        story_id: id,
                        progress: progress,
                        last_position: Math.round(scrollTop)
                    }, {
                        headers: getAuthHeaders()
                    });
                } catch (error) {
                    console.error('Failed to update progress:', error);
                }
            }, 5000);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (progressThrottleRef.current) {
                clearTimeout(progressThrottleRef.current);
            }
        };
    }, [story, id, getAuthHeaders]);

    const toggleBookmark = async () => {
        if (!user) {
            navigate('/prihlaseni');
            return;
        }

        try {
            if (isBookmarked) {
                await axios.delete(`${API}/bookmarks/${bookmarkId}`, {
                    headers: getAuthHeaders()
                });
                setIsBookmarked(false);
                setBookmarkId(null);
                toast.success('Povídka byla odebrána z uložených');
            } else {
                const response = await axios.post(`${API}/bookmarks`, {
                    story_id: id
                }, {
                    headers: getAuthHeaders()
                });
                setIsBookmarked(true);
                setBookmarkId(response.data.id);
                toast.success('Povídka byla uložena');
            }
        } catch (error) {
            toast.error('Nepodařilo se upravit záložku');
        }
    };

    useEffect(() => {
        const handleCopy = (e) => {
            e.preventDefault();
            toast.info('Kopírování textu není povoleno');
        };

        const handleContextMenu = (e) => {
            if (contentRef.current?.contains(e.target)) {
                e.preventDefault();
            }
        };

        document.addEventListener('copy', handleCopy);
        document.addEventListener('cut', handleCopy);
        document.addEventListener('contextmenu', handleContextMenu);

        return () => {
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('cut', handleCopy);
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-xl italic text-muted-foreground">Načítání...</p>
            </div>
        );
    }

    const displayStory = story || preview;

    if (!displayStory) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <p className="text-xl italic mb-4">Povídka nenalezena</p>
                <Link to="/povidky" className="italic hover:underline">
                    Zpět na povídky
                </Link>
            </div>
        );
    }

    const readerStyles = {
        fontFamily: settings.font_family,
        fontSize: `${settings.font_size}px`,
        lineHeight: settings.line_height,
    };

    return (
        <div className="min-h-screen" data-testid="story-detail-page">
            {/* Reading Progress Bar */}
            {story && (
                <div className="fixed top-0 left-0 right-0 z-50 h-px bg-foreground/10">
                    <div 
                        className="h-full bg-foreground transition-all duration-300"
                        style={{ width: `${readingProgress * 100}%` }}
                    />
                </div>
            )}

            {/* Header with title */}
            <header className="text-center py-12 md:py-16">
                <h1 className="text-3xl md:text-4xl italic mb-2">
                    {displayStory.author_name}: {displayStory.title}
                </h1>
                <div className="w-full max-w-3xl mx-auto h-px bg-foreground/30 mt-8" />
            </header>

            {/* Content */}
            <section className="max-w-2xl mx-auto px-6 pb-16">
                {/* Action buttons */}
                {story && (
                    <div className="flex items-center justify-end gap-2 mb-8">
                        <button
                            onClick={toggleBookmark}
                            className="p-2 hover:bg-secondary rounded-full transition-colors"
                            data-testid="bookmark-btn"
                            aria-label={isBookmarked ? 'Odebrat z uložených' : 'Uložit'}
                        >
                            {isBookmarked ? (
                                <BookmarkCheck className="w-5 h-5" />
                            ) : (
                                <Bookmark className="w-5 h-5" />
                            )}
                        </button>
                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            className="p-2 hover:bg-secondary rounded-full transition-colors"
                            data-testid="reader-settings-btn"
                            aria-label="Nastavení čtení"
                        >
                            <Settings className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* Text content */}
                <div ref={contentRef}>
                    {story ? (
                        <>
                            <div 
                                className="reader-content prose prose-lg max-w-none mb-12"
                                style={readerStyles}
                                dangerouslySetInnerHTML={{ __html: story.full_html }}
                                data-testid="story-full-content"
                            />
                            {/* Illustration at the end */}
                            {displayStory.cover_image_url && (
                                <div className="mt-12">
                                    <img 
                                        src={displayStory.cover_image_url} 
                                        alt={displayStory.title}
                                        className="w-full max-w-md mx-auto"
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div data-testid="story-preview">
                            <p 
                                className="text-lg leading-relaxed mb-12"
                                style={readerStyles}
                            >
                                {displayStory.preview_text}
                            </p>

                            {/* Paywall */}
                            <div className="text-center py-12 border-t border-foreground/20">
                                <Lock className="w-8 h-8 mx-auto mb-6 text-muted-foreground" />
                                <h2 className="text-2xl italic mb-4">
                                    Pokračujte ve čtení
                                </h2>
                                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                    Pro přístup k celé povídce si aktivujte předplatné Listovny. 
                                    První měsíc zdarma!
                                </p>
                                <Link to={user ? "/ucet" : "/registrace"}>
                                    <Button variant="outline" className="italic" data-testid="paywall-cta">
                                        {user ? "Aktivovat předplatné" : "Registrovat se zdarma"}
                                    </Button>
                                </Link>
                            </div>

                            {/* Illustration at the end for preview too */}
                            {displayStory.cover_image_url && (
                                <div className="mt-12">
                                    <img 
                                        src={displayStory.cover_image_url} 
                                        alt={displayStory.title}
                                        className="w-full max-w-md mx-auto"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Reader Settings Panel */}
            {showSettings && (
                <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
                    <div 
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setShowSettings(false)}
                    />
                    <div className="relative bg-background w-full md:max-w-md p-8 animate-fade-in" data-testid="reader-settings-panel">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-xl italic">Nastavení čtení</h3>
                            <button
                                onClick={() => setShowSettings(false)}
                                className="p-2 hover:bg-secondary rounded-full"
                                aria-label="Zavřít"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-8">
                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    Písmo
                                </label>
                                <Select 
                                    value={settings.font_family}
                                    onValueChange={(value) => updateSettings({ ...settings, font_family: value })}
                                >
                                    <SelectTrigger data-testid="font-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Cormorant Garamond">Cormorant Garamond</SelectItem>
                                        <SelectItem value="Georgia">Georgia</SelectItem>
                                        <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    Velikost písma: {settings.font_size}px
                                </label>
                                <Slider
                                    value={[settings.font_size]}
                                    onValueChange={(value) => updateSettings({ ...settings, font_size: value[0] })}
                                    min={14}
                                    max={28}
                                    step={1}
                                    data-testid="font-size-slider"
                                />
                            </div>

                            <div>
                                <label className="text-sm text-muted-foreground mb-2 block">
                                    Řádkování: {settings.line_height}
                                </label>
                                <Slider
                                    value={[settings.line_height]}
                                    onValueChange={(value) => updateSettings({ ...settings, line_height: value[0] })}
                                    min={1.4}
                                    max={2.4}
                                    step={0.1}
                                    data-testid="line-height-slider"
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">
                                    Tmavý režim
                                </span>
                                <button
                                    onClick={() => updateSettings({ ...settings, dark_mode: !settings.dark_mode })}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${
                                        settings.dark_mode ? 'bg-foreground' : 'bg-foreground/20'
                                    }`}
                                    data-testid="dark-mode-switch"
                                >
                                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-background transition-transform ${
                                        settings.dark_mode ? 'translate-x-7' : 'translate-x-1'
                                    }`} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
